import {
  ArrivalReadingText, Badge, Card, ChildAvatar, DataTable, DateInput, ErrorState, Grid, Notice, PageHeader, Row, Select, Stack, Stat,
  useApi, useI18n,
  type AttendanceStatus,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LABELS, TONES } from '../attendanceStatus';
import { AbsenceNoticesCard, AttendanceActions } from '../components/AttendanceCards';

/**
 * The console's view of attendance (§4.9).
 *
 * This is where an owner answers "who is not here, and why", which is what the dashboard's absent tile links
 * through to, with the status already in the query string (FR-ADM-03). The daily round of check-ins belongs to the
 * staff app, in a layout a thumb reaches; here the administration checks a child in or out when the front desk did
 * not, and corrects a recorded time with a reason (FR-ATT-06). The absence notices for the coming days are below.
 */
export function Attendance() {
  const api = useApi();
  const { t, formatDate, formatTime } = useI18n();
  const [params, setParams] = useSearchParams();
  const [classId, setClassId] = useState<number | ''>('');

  const date = params.get('date') ?? undefined;
  const status = (params.get('status') as AttendanceStatus | null) ?? null;

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active' }],
    queryFn: () => api.classes.list({ status: 'Active' }),
  });

  const board = useQuery({
    queryKey: ['attendance', 'board', date, classId],
    queryFn: () => api.attendance.board({
      ...(date ? { date } : {}),
      ...(classId === '' ? {} : { classId }),
    }),
  });

  const rows = (board.data?.rows ?? []).filter((row) => !status || row.status === status);

  return (
    <Stack>
      <PageHeader
        title={t('attendance.title')}
        lede={board.data ? formatDate(board.data.date) : undefined}
      />

      {board.data && (
        <Grid>
          <Stat label={t('attendance.present')} value={board.data.present} />
          <Stat label={t('attendance.absent')} value={board.data.absent} />
          <Stat label={t('attendance.notified')} value={board.data.notifiedAbsent} />
          <Stat label={t('attendance.expected')} value={board.data.expected} />
        </Grid>
      )}

      {board.data && !board.data.operatingDay && (
        <Notice tone="info">{t('attendance.closedDay')}</Notice>
      )}
      {board.data?.operatingDay && !board.data.cutoffPassed && (
        <Notice tone="info">{t('attendance.beforeCutoff')}</Notice>
      )}

      <Row>
        <DateInput
          value={date ?? ''}
          aria-label={t('audit.when')}
          onChange={(value) => {
            const next = new URLSearchParams(params);
            if (value) next.set('date', value); else next.delete('date');
            setParams(next);
          }}
        />

        <Select
          aria-label={t('class.one')}
          value={classId}
          onChange={(event) => setClassId(event.target.value === '' ? '' : Number(event.target.value))}
        >
          <option value="">{t('nav.classes')}</option>
          {(classes.data?.items ?? []).map((klass) => (
            <option key={klass.id} value={klass.id}>{klass.name}</option>
          ))}
        </Select>

        <Select
          aria-label={t('action.filter')}
          value={status ?? ''}
          onChange={(event) => {
            const next = new URLSearchParams(params);
            if (event.target.value) next.set('status', event.target.value); else next.delete('status');
            setParams(next);
          }}
        >
          <option value="">{t('action.filter')}</option>
          {(Object.keys(LABELS) as AttendanceStatus[]).map((value) => (
            <option key={value} value={value}>{t(LABELS[value])}</option>
          ))}
        </Select>
      </Row>

      {board.isError && <ErrorState error={board.error} onRetry={() => void board.refetch()} />}

      <Card flush>
        <DataTable
          caption={t('attendance.board')}
          rows={rows}
          rowKey={(row) => row.childId}
          isLoading={board.isPending}
          empty={t('state.empty')}
          columns={[
            {
              key: 'child',
              header: t('child.one'),
              render: (row) => (
                <Row>
                  <ChildAvatar child={row} photoUrl={row.photoUrl} size="sm" allergies={row.allergyMarks} />
                  <span>{row.preferredName ?? `${row.firstName} ${row.lastName}`}</span>
                </Row>
              ),
            },
            { key: 'class', header: t('class.one'), render: (row) => row.className ?? '—' },
            {
              key: 'status',
              header: t('audit.action'),
              render: (row) => <Badge tone={TONES[row.status]}>{t(LABELS[row.status])}</Badge>,
            },
            {
              key: 'in',
              header: t('attendance.checkIn'),
              render: (row) => row.checkInAt
                ? (
                  <span className="dg-stack dg-stack--tight">
                    <span>{formatTime(row.checkInAt)}</span>
                    <ArrivalReadingText row={row} />
                  </span>
                )
                : '—',
            },
            {
              key: 'out',
              header: t('attendance.checkOut'),
              render: (row) => row.checkOutAt ? formatTime(row.checkOutAt) : '—',
            },
            {
              key: 'collected',
              header: t('pickups.title'),
              render: (row) => (
                <span className="dg-stack dg-stack--tight">
                  <span>{row.pickedUpByName ?? '—'}</span>
                  {/* An exception is flagged here as well as notified, so it survives the notification
                      being missed (FR-ATT-05). */}
                  {row.pickupOverrideReason && (
                    <Badge tone="warning">{t('attendance.pickupOverride')}</Badge>
                  )}
                </span>
              ),
            },
            { key: 'reason', header: t('attendance.absenceReason'), render: (row) => row.absenceReason ?? '—' },
            {
              key: 'actions',
              header: '',
              render: (row) => board.data ? <AttendanceActions row={row} date={board.data.date} /> : null,
            },
          ]}
        />
      </Card>

      <AbsenceNoticesCard />
    </Stack>
  );
}
